import { useToast } from '@chakra-ui/react';
import { useReducer, useRef } from 'react';

import { getClient } from '@/lib/restClient';
import {
  AuthStoresService,
  ChangePasswordForm,
} from '@/services/AuthStores/AuthStoresService';

import { validateAuthStorePassword } from '../../auth-stores/components/AuthStoreSidebarForm/validate';

const initialState = {
  isPending: false,
  isShowNewPassword: false,
  isShowNewConfirmPassword: false,
  isNewPasswordEmpty: false,
  isNewConfirmPasswordEmpty: false,
  isPasswordsUnmatched: false,
  requestError: '',
  newPasswordInvalidError: '',
};

const IS_PENDING = 'IS_PENDING' as const;
const IS_SHOW_NEW_PASSWORD = 'IS_SHOW_NEW_PASSWORD' as const;
const IS_SHOW_NEW_CONFIRM_PASSWORD = 'IS_SHOW_NEW_CONFIRM_PASSWORD' as const;
const IS_NEW_PASSWORD_EMPTY = 'IS_NEW_PASSWORD_EMPTY' as const;
const IS_NEW_CONFIRM_PASSWORD_EMPTY = 'IS_NEW_CONFIRM_PASSWORD_EMPTY' as const;
const IS_PASSWORDS_UNMATCHED = 'IS_PASSWORDS_UNMATCHED' as const;
const REQUEST_ERROR = 'REQUEST_ERROR' as const;
const NEW_PASSWORD_INVALID_ERROR = 'NEW_PASSWORD_INVALID_ERROR' as const;
const RESET_FORM = 'RESET_FORM' as const;

const setIsPendingAction = (flag: boolean) => ({
  type: IS_PENDING,
  payload: flag,
});
const setIsShowNewPasswordAction = (flag: boolean) => ({
  type: IS_SHOW_NEW_PASSWORD,
  payload: flag,
});
const setIsShowNewConfirmPasswordAction = (flag: boolean) => ({
  type: IS_SHOW_NEW_CONFIRM_PASSWORD,
  payload: flag,
});
const setIsNewPasswordEmptyAction = (flag: boolean) => ({
  type: IS_NEW_PASSWORD_EMPTY,
  payload: flag,
});
const setIsNewConfirmPasswordEmptyAction = (flag: boolean) => ({
  type: IS_NEW_CONFIRM_PASSWORD_EMPTY,
  payload: flag,
});
const setIsPasswordsUnmatchedAction = (flag: boolean) => ({
  type: IS_PASSWORDS_UNMATCHED,
  payload: flag,
});
const setRequestErrorAction = (errMessage: string) => ({
  type: REQUEST_ERROR,
  payload: errMessage,
});
const setNewPasswordInvalidErrorAction = (errMessage: string) => ({
  type: NEW_PASSWORD_INVALID_ERROR,
  payload: errMessage,
});

const resetFormAction = () => ({
  type: RESET_FORM,
});

type Actions =
  | ReturnType<typeof setIsPendingAction>
  | ReturnType<typeof setIsShowNewPasswordAction>
  | ReturnType<typeof setIsShowNewConfirmPasswordAction>
  | ReturnType<typeof setIsNewPasswordEmptyAction>
  | ReturnType<typeof setIsNewConfirmPasswordEmptyAction>
  | ReturnType<typeof setIsPasswordsUnmatchedAction>
  | ReturnType<typeof setRequestErrorAction>
  | ReturnType<typeof setNewPasswordInvalidErrorAction>
  | ReturnType<typeof resetFormAction>;

const reducer = (state: typeof initialState, action: Actions) => {
  switch (action.type) {
    case IS_PENDING: {
      if (action.payload === state.isPending) return state;
      return {
        ...state,
        isPending: action.payload,
      };
    }
    case IS_SHOW_NEW_PASSWORD: {
      if (action.payload === state.isShowNewPassword) return state;
      return {
        ...state,
        isShowNewPassword: action.payload,
      };
    }
    case IS_SHOW_NEW_CONFIRM_PASSWORD: {
      if (action.payload === state.isShowNewConfirmPassword) return state;
      return {
        ...state,
        isShowNewConfirmPassword: action.payload,
      };
    }
    case IS_NEW_PASSWORD_EMPTY: {
      if (action.payload === state.isNewPasswordEmpty) return state;
      return {
        ...state,
        isNewPasswordEmpty: action.payload,
      };
    }
    case IS_NEW_CONFIRM_PASSWORD_EMPTY: {
      if (action.payload === state.isNewConfirmPasswordEmpty) return state;
      return {
        ...state,
        isNewConfirmPasswordEmpty: action.payload,
      };
    }
    case IS_PASSWORDS_UNMATCHED: {
      if (action.payload === state.isPasswordsUnmatched) return state;
      return {
        ...state,
        isPasswordsUnmatched: action.payload,
      };
    }
    case REQUEST_ERROR: {
      if (action.payload === state.requestError) return state;
      return {
        ...state,
        requestError: action.payload,
      };
    }
    case NEW_PASSWORD_INVALID_ERROR: {
      if (action.payload === state.newPasswordInvalidError) return state;
      return {
        ...state,
        newPasswordInvalidError: action.payload,
      };
    }
    case RESET_FORM: {
      return initialState;
    }
    default:
      return state;
  }
};

export const useChangePasswordDialog = (
  authStoreId: number,
  setIsModalOpen: (x: boolean) => void
) => {
  const authStoresService = new AuthStoresService(getClient());
  const toast = useToast();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    isShowNewPassword,
    isShowNewConfirmPassword,
    isNewPasswordEmpty,
    isNewConfirmPasswordEmpty,
  } = state;

  const newRef = useRef<HTMLInputElement>(null);
  const newConfirmRef = useRef<HTMLInputElement>(null);

  const handleShowNewPassword = () =>
    dispatch(setIsShowNewPasswordAction(!isShowNewPassword));

  const handleShowNewConfirmPassword = () =>
    dispatch(setIsShowNewConfirmPasswordAction(!isShowNewConfirmPassword));

  const resetFormState = () => dispatch(resetFormAction());

  const handleOnBlurNewPasswords = () => {
    if (!newRef.current?.value || !newConfirmRef.current?.value) {
      dispatch(setIsPasswordsUnmatchedAction(false));
    }
    if (newRef.current?.value !== newConfirmRef.current?.value) {
      dispatch(setIsPasswordsUnmatchedAction(true));
    }
    if (newRef.current?.value.trim() && isNewPasswordEmpty) {
      dispatch(setIsNewPasswordEmptyAction(false));
    }
    if (newConfirmRef.current?.value.trim() && isNewConfirmPasswordEmpty) {
      dispatch(setIsNewConfirmPasswordEmptyAction(false));
    }
    const { error } = validateAuthStorePassword.safeParse(
      newRef.current?.value
    );
    if (error) {
      const errorMessages = error?.issues.map((x) => x.message).join('\n');
      dispatch(setNewPasswordInvalidErrorAction(errorMessages));
    } else {
      dispatch(setNewPasswordInvalidErrorAction(''));
    }
    dispatch(setIsPasswordsUnmatchedAction(false));
  };

  const handleSubmit = () => {
    const newPassword = newRef.current?.value;
    const newPasswordConfirm = newConfirmRef.current?.value;
    if (!newPassword?.trim() || !newPasswordConfirm?.trim()) {
      if (!newPassword?.trim()) {
        dispatch(setIsNewPasswordEmptyAction(true));
      }
      if (!newPasswordConfirm?.trim()) {
        dispatch(setIsNewConfirmPasswordEmptyAction(true));
      }
      return;
    }

    if (newRef.current?.value !== newConfirmRef.current?.value) {
      dispatch(setIsPasswordsUnmatchedAction(true));
      return;
    }
    const { error } = validateAuthStorePassword.safeParse(newPassword);
    if (error) {
      const errorMessages = error?.issues.map((x) => x.message).join('\n');
      dispatch(setNewPasswordInvalidErrorAction(errorMessages));
      return;
    }
    dispatch(setRequestErrorAction(''));
    // httpリクエスト
    //   - 成功時 → ダイアログを閉じる
    //   - エラー時 → エラー表示
    authStoresService
      .changePassword({
        auth_store_id: authStoreId,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      } as ChangePasswordForm)
      .then(() => {
        handleCancel();
        toast({
          title: 'パスワードを変更しました',
          status: 'success',
          duration: 3000,
          isClosable: false,
          position: 'top',
        });
      })
      .catch((e) => {
        dispatch(setRequestErrorAction(e.message));
      });
  };

  const handleCancel = () => {
    resetFormState();
    setIsModalOpen(false);
  };

  return {
    ...state,
    // refs
    newRef,
    newConfirmRef,
    // event handlers
    handleShowNewPassword,
    handleShowNewConfirmPassword,
    handleOnBlurNewPasswords,
    handleSubmit,
    handleCancel,
  };
};
